import FeatureGates from '@atlaskit/feature-gate-js-client';
import {
	type ExperimentConfig,
	STATSIG_SEARCH_PLATFORM_JIRA_LAYER_TENANTS_BASED,
} from '@atlassian/search-experiment';
import { useRovoEntitlementResource } from '@atlassian/jira-router-resources-rovo-entitlement/src/services/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';

const ISSUE_NAVIGATOR_URL = `/issues/?jql=${encodeURIComponent(
	'created >= -30d order by created DESC',
)}`;

const buildJiraSearchUrl = (query: string) =>
	`/jira/search?searchString=${encodeURIComponent(query)}`;

const buildSmartQueryAdvancedSearchUrl = (query: string) =>
	`/secure/QuickSearch.jspa?searchString=${encodeURIComponent(query)}`;

const buildJQLAdvancedIssueUrl = (jql: string) => `/issues/?jql=${jql}`;

export const buildJiraAdvancedSearchPath = (query: string, jql?: string) => {
	const trimmedQuery = query.trim();
	if (!trimmedQuery) {
		// If query is empty, we utilise a spa transition with ISSUE_NAVIGATOR_URL
		return query === '' ? ISSUE_NAVIGATOR_URL : buildSmartQueryAdvancedSearchUrl(query);
	}

	if (jql) {
		// if JQL is provided, we build the work items URL with JQL
		return buildJQLAdvancedIssueUrl(jql);
	}

	// we navigate to the search page to handle the single work item page in case the query is a work item key
	return buildJiraSearchUrl(trimmedQuery);
};

export const buildJiraAdvancedSearchUrl = (query: string, jql?: string, siteUrl?: string) => {
	return new URL(buildJiraAdvancedSearchPath(query, jql), siteUrl ?? '');
};

export const useQuickFindEnabled = (experimentConfig?: ExperimentConfig) => {
	const rovoEntitlement = useRovoEntitlementResource();

	// Enable quick find by feature flag as priority to
	// allow easier internal dogfooding
	if (fg('rovo_search_jira_enable_quick_find')) {
		return true;
	}

	if (!rovoEntitlement?.data?.isRovoEnabled) {
		return false;
	}

	let isExperimentEnabled = false;
	let isRovoSearchExperienceEnabledByExperiment = false;
	if (!experimentConfig) {
		const layer = FeatureGates.getLayer(STATSIG_SEARCH_PLATFORM_JIRA_LAYER_TENANTS_BASED, {
			fireLayerExposure: false,
		});
		const uiFeatures = layer.get<{ rovoSearchExperienceEnabled: boolean }>('uiFeatures', {
			rovoSearchExperienceEnabled: false,
		});
		isExperimentEnabled = layer.get('enabled', false);
		isRovoSearchExperienceEnabledByExperiment = uiFeatures.rovoSearchExperienceEnabled;
	} else {
		isExperimentEnabled = experimentConfig.combinedExperiment?.enabled ?? false;
		isRovoSearchExperienceEnabledByExperiment =
			experimentConfig.combinedExperiment?.uiFeatures?.rovoSearchExperienceEnabled ?? false;
	}

	if (!isExperimentEnabled) {
		return false;
	}

	return isRovoSearchExperienceEnabledByExperiment;
};
